<template>
    <div>
        <ListData :title="$t('menu.songs')" 
            :dataTable="songsTable" 
            :data="songs" 
            :totalPages="totalPages"
            :loading="loading"
            :allowMultipleSelect="true"
            :isRowSelectable="canDeleteSong"
            @fetchData="fetchData"
            @addNewData="createSongPage"
            @deleteData="deleteSelected"
        >
            <template #extraHeaderActions>
                <button
                    type="button"
                    @click="openBatchImportModal"
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-3.5 sm:py-2 border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/40 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 text-xs sm:text-sm font-semibold rounded-lg shadow-sm transition-all duration-150 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    :title="$t('songBatchImport.buttonTooltip')"
                >
                    <svg class="w-4 h-4 sm:w-4.5 sm:h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span>{{ $t('songBatchImport.button') }}</span>
                </button>
            </template>
        </ListData>
        <TagsEditor ref="tagsEditor" />
        <ArtistInfoSidePanel ref="artistInfoSidePanel" />
        <TriadResourceShareModal ref="shareModal" />
        <SongBatchImportModal ref="batchImportModal" @imported="onSongsImported" />
    </div>
</template>

<script setup>
import { ref, computed, h, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import TriadEditIcon from "@/components/icons/TriadEditIcon.vue";
import TriadExpandIcon from "@/components/icons/TriadExpandIcon.vue";
import TriadRemoveIcon from "@/components/icons/TriadRemoveIcon.vue";
import TriadResourceShareModal from '@/components/common/TriadResourceShareModal.vue';
import SongBatchImportModal from '@/components/song/SongBatchImportModal.vue';
import TagsViewer from '@/components/common/elements/TagsViewer.vue';
import TagsEditor from '@/components/common/TriadTagsEditor.vue';
import Pagination from '@/models/pagination';
import toastStore from '@/store/toast.store';
import dialogStore from '@/store/dialog.store';
import songPreviewStore from '@/store/song.preview.store';
import searchModalStore from '@/store/search.modal.store';
import TriadFavoriteIcon from '@/components/icons/TriadFavoriteIcon.vue';
import TriadShareIcon from '@/components/icons/TriadShareIcon.vue';
import userStore, { verifyIam } from '@/store/user.store';
import { useRouter } from 'vue-router';
import { appEvents } from '@/store/app.events';
import { cleanLyricPreview } from '@/service/utility';

const router = useRouter();
const { t } = useI18n();
const artistInfoSidePanel = ref(null);
const shareModal = ref(null);
const batchImportModal = ref(null);

const openBatchImportModal = () => {
    batchImportModal.value?.open();
};

const onSongsImported = () => {
    fetchData();
};

const loading = ref(true);
const reloadingTable = ref(false);
const filter = ref({
    pagination: new Pagination(1,10)
});

const totalPages = ref(0);

const canManageAllSongs = computed(() => {
    try {
        return verifyIam(userStore.state.userInfo, '/song');
    } catch {
        return false;
    }
});

const canDeleteSong = (element) => {
    if (!element) return false;
    if (canManageAllSongs.value) return true;
    return element.owner !== false;
};

const songsTable = computed(() => ({
    columns: [

        { title: t('table.title'), 
            component: (element) => {
                const titleEl = h('div', { class: 'font-medium text-gray-900 dark:text-white truncate' }, element.title);
                if (element.owner === false) {
                    const badgeEl = h('span', { 
                        class: 'inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-300 rounded truncate'
                    }, t('form.sharedWithYou'));
                    return h('div', { class: 'flex flex-col min-w-0 overflow-hidden' }, [titleEl, badgeEl]);
                }
                return titleEl;
            },
            rowStyle: 'w-3/5 md:w-1/5 md:min-w-[150px] overflow-hidden'
        },
        { title: t('table.artist'), 
            component: (element) => {
                const artistName = element.artist?.name || 'Unknown';
                return h('button', {
                    class: 'text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:underline font-medium focus:outline-none text-left truncate w-full block',
                    onClick: (e) => {
                        e.stopPropagation();
                        if (element.artist?.id || element.artist?._id) {
                            artistInfoSidePanel.value.open(element.artist.id || element.artist._id);
                        }
                    }
                }, artistName);
            },
            rowStyle: 'w-2/5 md:w-1/6 md:min-w-[120px] overflow-hidden'
        },
        { 
            title: t('table.lyric'), 
            field: 'lyric', 
            sortable: false, 
            format: (element) => cleanLyricPreview(element.lyric, 120),
            rowStyle: 'w-1/3 min-w-[200px] text-pretty hidden lg:table-cell text-gray-500 dark:text-gray-400 text-xs'
        },
        { title: t('table.tag'), 
            component: (element) => {
                return h(TagsViewer, { 
                    element: element,
                    tags: element.tags || [],
                    visibleAddButton: true
                })
            },
            rowStyle: 'w-1/5 min-w-[160px] text-pretty hidden xl:table-cell'
        },
        { title: t('table.composer'), field: 'composer', sortable: false, rowStyle: 'w-24 max-w-[120px] truncate hidden xl:table-cell text-gray-500'},
        {
            action: (element) => {
                songPreviewStore.dispatch('open', element.id)
            },
            component: TriadExpandIcon,
            properties: () => ({
                tooltip: t('table.view')
            }),
            rowStyle: 'w-[50px] max-w-[50px]'
        },
        {
            properties: (element) => ({
                tooltip: t('searchModal.favorite'),
                isFavorite: searchModalStore.state.favorites.some(f => (f.id || f._id) === element.id)
            }),
            component: TriadFavoriteIcon,
            action: (element) => {
                searchModalStore.dispatch('toggleFavorite', element);
            },
            rowStyle: 'w-[50px] max-w-[50px]'
        },
        {
            properties: () => ({
                tooltip: 'Share Song'
            }),
            disabled: (element) => element.canEdit === false,
            component: TriadShareIcon,
            action: (element) => {
                shareModal.value?.open('song', element.id, element.title, element.artist?.id || element.artist?._id);
            },
            rowStyle: 'w-[50px] max-w-[50px]'
        },
        { 
            properties: () => ({
                tooltip: t('table.edit')
            }),
            disabled: (element) => element.canEdit === false,
            component: TriadEditIcon,
            action: (element) => {
                router.push({path: `/song/${element.id}`})
            },
            rowStyle: 'w-[50px] max-w-[50px]'
        },

        { 
            properties: () => ({
                tooltip: t('table.delete')
            }),
            disabled: (element) => !canDeleteSong(element),
            component: TriadRemoveIcon,
            action: (element) => {
                if (!canDeleteSong(element)) {
                    toastStore.dispatch('addMessage', { message: t('artistInfo.notOwnerAction'), icon: 'warning', ttl: 3000 });
                    return;
                }
                removeSong(element.id)
            },
            rowStyle: 'w-[50px] max-w-[50px]'
        },
        
    ]
}));
const songs = ref([])

const fetchData = async (f) => {
    filter.value = f || filter.value;
    const query = Object.entries(filter.value).map( f => {
            // Multi-tag: repeated `tag=` params (backend AND semantics)
            if (f[0] === 'tag' && Array.isArray(f[1])) {
                return f[1].map(t => `tag=${encodeURIComponent(t)}`).join('&');
            }
            if (f[1] !== null && typeof f[1] === 'object') {
                if (typeof f[1].toString === 'function' && f[1].toString !== Object.prototype.toString) {
                    return f[1].toString()
                } else {
                    return Object.entries(f[1]).map(e => `${e[0]}=${e[1]}`).join('&')
                }
            } else {
                return `${f[0]}=${encodeURIComponent(f[1])}`
            }
        }).join('&');
    const response = await fetch(`/api/song?${query}`)
    if  (response.status === 200) {
        const resData = await response.json()
        songs.value = resData.data ? resData : { data: resData, totalPages: 1, total: resData.length }
        totalPages.value = songs.value.totalPages || 0
    } else {
        toastStore.dispatch('addMessage', {message: t('toast.errorFetchingSongs'), icon: 'error'})
    }
    loading.value = false;
    reloadingTable.value = false;
}

const removeSong = async (id) => {
    if (await dialogStore.dispatch('confirm', 'Are you sure you want to delete this song?')) {
        reloadingTable.value = true;
        const response = await fetch(`/api/song/${id}`, {
            method: 'DELETE'
        })
        if (response.status === 200) {
            toastStore.dispatch('addMessage', {message: t('toast.songDeleted'), icon: 'success'})
            fetchData()
        } else {
            toastStore.dispatch('addMessage', {message: t('toast.errorDeletingSong'), icon: 'error'})
            reloadingTable.value = false;
        }
    }
}

const deleteSelected = async (elements) => {
    const owned = (elements || []).filter(canDeleteSong);
    if (owned.length === 0) {
        toastStore.dispatch('addMessage', { message: t('artistInfo.notOwnerAction'), icon: 'warning', ttl: 3000 });
        return;
    }
    if (owned.length < (elements || []).length) {
        toastStore.dispatch('addMessage', { message: t('artistInfo.notOwnerAction'), icon: 'warning', ttl: 3000 });
    }
    if (await dialogStore.dispatch('confirm', 'Are you sure you want to delete all these songs?')) {
        reloadingTable.value = true;
        const response = await fetch(`/api/song/delete`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ids: owned.map(e => e.id || e._id)
            })
        })
        if (response.status === 200) {
            toastStore.dispatch('addMessage', {message: t('toast.songsDeleted'), icon: 'success'})
            fetchData()
        } else {
            toastStore.dispatch('addMessage', {message: t('toast.errorDeletingSongs'), icon: 'error'})
            reloadingTable.value = false;
        }
    }  
}

const createSongPage = () => {
    router.push({path: `/song/new`})
}


const tagsEditor = ref(null);

const createTag = async (element) => {
    if (element.title === undefined) return;
    if (!element.tag) element.tag = [];
    const editorTags = element.tag.map(t => typeof t === 'object' && t !== null ? t : { name: t });
    const aiContext = { 
        type: 'song', 
        title: element.title, 
        artist: element.artist?.name || element.artist, 
        composer: (element.composers || element.composer || []).map(c => typeof c === 'object' ? c.name : c).join(', '), 
        lyric: element.lyric || element.lyrics 
    };
    const newTags = await tagsEditor.value.open(editorTags, aiContext);
    if (newTags) {
        toastStore.dispatch('addMessage', {message: 'Saving tags', icon: 'info'});
        const payload = {
            ...element,
            composer: (element.composers || element.composer || []).map(c => ({ name: typeof c === 'object' ? c.name : c })),
            tag: newTags,
            tags: newTags,
            is_public: element.isPublic
        };
        const response = await fetch('/api/song/' + element.id, {
            method: 'PUT',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        if (response.status === 200) {
            toastStore.dispatch('addMessage', {message: t('toast.tagsUpdated'), icon: 'success'})
            fetchData();
        } else {
            toastStore.dispatch('addMessage', {message: t('toast.errorUpdatingTags'), icon: 'error'})
        }
    }
}

onMounted(() => {
    appEvents.on('tagsviewer.createTag', createTag)
    searchModalStore.dispatch('fetchFavorites');
    fetchData();
})
onUnmounted(() => {
    appEvents.off('tagsviewer.createTag', createTag)
})

</script>